import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Hourglass, CheckCircle, XCircle, ClipboardList, PlusCircle, ImageIcon, X } from 'lucide-react';

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [previewProof, setPreviewProof] = useState(null); // deposit whose screenshot is open in the lightbox

  const fetchDeposits = useCallback(() => {
    setLoading(true);
    api.get('/deposits/all').then(r => setDeposits(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this deposit? The amount will be credited to the user\'s wallet immediately.')) return;
    setActionLoading(id + '_approve');
    try {
      const res = await api.put(`/deposits/${id}/approve`);
      toast.success(res.data.message);
      fetchDeposits();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const note = prompt('Reason for rejection:') || '';
    setActionLoading(id + '_reject');
    try {
      const res = await api.put(`/deposits/${id}/reject`, { adminNote: note });
      toast.success(res.data.message);
      fetchDeposits();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setActionLoading(null); }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const filtered = deposits.filter(d => filter === 'all' || d.status === filter);
  const counts = {
    pending: deposits.filter(d => d.status === 'pending').length,
    approved: deposits.filter(d => d.status === 'approved').length,
    rejected: deposits.filter(d => d.status === 'rejected').length,
  };
  const totalPendingAmount = deposits.filter(d => d.status === 'pending').reduce((sum, d) => sum + d.amount, 0);

  return (
    <DashboardLayout>
      <h1 className="page-title">Deposit Requests</h1>
      <p className="page-subtitle">Approve funds users have paid in, so they can purchase a package</p>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pending', value: counts.pending, color: '#f59e0b', icon: Hourglass },
          { label: 'Approved', value: counts.approved, color: '#22c55e', icon: CheckCircle },
          { label: 'Rejected', value: counts.rejected, color: '#ef4444', icon: XCircle },
          { label: 'Pending Amount', value: formatCurrency(totalPendingAmount), color: 'var(--primary)', icon: PlusCircle },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.1rem' }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="tab-bar" style={{ marginBottom: 20 }}>
        {[['pending', Hourglass, 'Pending'], ['approved', CheckCircle, 'Approved'], ['rejected', XCircle, 'Rejected'], ['all', ClipboardList, 'All']].map(([id, Icon, label]) => (
          <button key={id} className={`tab-btn ${filter === id ? 'active' : ''}`} onClick={() => setFilter(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon size={14} /> {label} ({id === 'all' ? deposits.length : counts[id] || 0})
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><PlusCircle size={40} /></div><h3>No deposit requests found</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Date</th><th>User</th><th>Amount</th><th>Method</th><th>UTR</th>
                    <th>Paid To</th><th>Proof</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d._id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(d.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{d.user?.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{d.user?.mobile}</div>
                      </td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(d.amount)}</td>
                      <td>{d.paymentMethod}</td>
                      <td style={{ fontSize: '0.8rem' }}>{d.utrNumber || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.paymentWallet?.label || '—'}</td>
                      <td>
                        {d.paymentProof ? (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', padding: '4px 8px' }}
                            onClick={() => setPreviewProof(d)}
                          >
                            <ImageIcon size={13} /> View
                          </button>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${d.status === 'approved' ? 'success' : d.status === 'rejected' ? 'danger' : 'warning'}`}>
                          {d.status}
                        </span>
                        {d.adminNote && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{d.adminNote}</div>}
                      </td>
                      <td>
                        {d.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(d._id)}
                              disabled={actionLoading === d._id + '_approve'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === d._id + '_approve' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Approve</span>)}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(d._id)}
                              disabled={actionLoading === d._id + '_reject'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === d._id + '_reject' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={14} /> Reject</span>)}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {d.approvedAt ? new Date(d.approvedAt).toLocaleDateString('en-IN') : 'Processed'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {/* Payment proof lightbox */}
      {previewProof && (
        <div className="modal-overlay" onClick={() => setPreviewProof(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 className="card-title" style={{ margin: 0 }}>Payment Proof — {previewProof.user?.name}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setPreviewProof(null)} style={{ padding: '4px 8px' }}><X size={16} /></button>
            </div>
            <img src={previewProof.paymentProof} alt="Payment proof" style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border-gold)' }} />
            <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--text-light)' }}>
              Amount: <strong style={{ color: 'var(--primary)' }}>{formatCurrency(previewProof.amount)}</strong> &nbsp;|&nbsp; UTR: <strong>{previewProof.utrNumber || '—'}</strong>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}